import { Fragment, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Shield, Plus, RefreshCw, Settings, LogOut, AlertTriangle, AlertCircle, Info, Sparkles, Download, ChevronDown, PauseCircle, Lock, PiggyBank } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { BarChart, Bar, Rectangle, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { getCachedDashboardSummary, invalidateDashboardCache } from "@/lib/dashboardCache";
import { PRICING } from "@/lib/pricing";
import { toast } from "sonner";

function StatInfo({ children }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-gray-300 hover:text-gray-500 transition-colors"
          aria-label="More information"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 text-sm text-gray-600 leading-relaxed" side="top">
        {children}
      </PopoverContent>
    </Popover>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [orderForm, setOrderForm] = useState({ amount: "", retailer: "Amazon", description: "" });
  const [referral, setReferral] = useState(null);
  const loadDashboard = async () => {
    try {
      const res = await getCachedDashboardSummary();
      setSummary(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/auth", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDashboard(); }, []);
  useEffect(() => {
    api.getReferralLink().then((res) => setReferral(res.data)).catch(() => {});
  }, []);

  const handleReferralClick = () => {
    if (!referral?.url) return;
    api.trackReferralClick({
      amount_saved: summary?.money_saved ?? summary?.budget_remaining ?? null,
      source: "dashboard",
    }).catch(() => {});
    window.open(referral.url, "_blank", "noopener,noreferrer");
  };

  const handleSync = async () => {
    if (!summary?.gmail_connected) {
      toast.error("Connect your Gmail first in Settings");
      navigate("/settings");
      return;
    }
    setSyncing(true);
    try {
      const res = await api.syncGmail();
      toast.success(res.data.message);
      invalidateDashboardCache();
      loadDashboard();
    } catch (err) {
      const detail = err.response?.data?.detail || "Import failed";
      if (err.response?.status === 401 && detail.includes("expired")) {
        toast.error("Gmail authorization expired. Please reconnect in Settings.");
      } else {
        toast.error(detail);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleAddOrder = async (e) => {
    e.preventDefault();
    try {
      const res = await api.createOrder({
        amount: parseFloat(orderForm.amount),
        retailer: orderForm.retailer,
        description: orderForm.description || undefined,
      });
      toast.success("Purchase recorded");
      setShowAddOrder(false);
      setOrderForm({ amount: "", retailer: "Amazon", description: "" });
      invalidateDashboardCache();
      loadDashboard();
      // The purchase is saved and counting either way — this just raises the question now,
      // while they still remember, instead of leaving it for a later dashboard visit.
      if (res.data?.possible_duplicate) {
        toast("This looks like one we already have", {
          description: `We captured a $${res.data.possible_duplicate.amount?.toFixed(2)} ${res.data.possible_duplicate.retailer} purchase on ${res.data.possible_duplicate.order_date}. Both are counted for now — you can mark them as the same below.`,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add purchase");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await api.deleteOrder(orderId);
      toast.success("Purchase removed");
      invalidateDashboardCache();
      loadDashboard();
    } catch { toast.error("Failed to delete"); }
  };

  const handleResolveDuplicate = async (orderId, resolution) => {
    try {
      await api.resolveDuplicate(orderId, resolution);
      toast.success(resolution === "confirmed" ? "Merged — only counted once" : "Kept both");
      invalidateDashboardCache();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update that");
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await api.exportCSV();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cartcontrol_orders.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Shield className="w-8 h-8 text-navy animate-pulse" />
      </div>
    );
  }

  const pct = summary?.budget_percentage || 0;
  const budgetColor = pct >= 100 ? "bg-danger" : pct >= 75 ? "bg-warning" : "bg-sage";
  const budgetTextColor = pct >= 100 ? "text-danger" : pct >= 75 ? "text-warning" : "text-sage";

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center">
            <img src="/logo-lockup.svg" alt="CartControl" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            {summary?.plan === "premium" && (
              <Badge className="bg-navy text-white text-xs">PREMIUM</Badge>
            )}
            {summary?.plan === "trial" && summary?.has_access && (
              <Badge variant="outline" className="border-sage text-sage text-xs" data-testid="trial-badge">
                Trial — {summary.trial_days_remaining}d left
              </Badge>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-sm text-gray-600" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-gray-600">
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings-btn">
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportCSV} disabled={!summary?.has_access} data-testid="menu-export-btn">
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                  {!summary?.has_access && <Lock className="w-3 h-3 ml-auto text-gray-400" />}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-danger" data-testid="menu-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Trial Expired Gate */}
        {summary?.plan === "trial" && !summary?.has_access && (
          <div className="mb-6 p-10 rounded-lg border border-border bg-white text-center" data-testid="trial-expired-gate">
            <img src="/shield-icon.png" alt="" className="w-10 h-10 mx-auto mb-4 opacity-30" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2 font-display">Your free trial has ended</h2>
            <p className="text-sm text-gray-500 mb-7 max-w-md mx-auto leading-relaxed">
              You're now on <span className="font-medium text-gray-700">Basic (free)</span> — Amazon tracking, warnings, and your full history stay. Upgrade to Premium for eBay &amp; Walmart, Clarity Lock, an adjustable pause timer, and CSV export.
            </p>
            <Button className="h-11 px-8 bg-navy hover:bg-navy-hover active:bg-navy-active text-white font-medium text-sm" onClick={() => navigate("/pricing")} data-testid="trial-expired-cta">
              Upgrade to Premium — {PRICING.priceWithInterval}
            </Button>
          </div>
        )}

        {/* Alerts */}
        {summary?.alerts?.map((alert, i) => (
          <div key={i} className={`mb-4 p-4 rounded-lg border flex items-center gap-3 ${alert.type === "error" ? "bg-danger/10 border-danger/30 text-danger" : alert.type === "warning" ? "bg-reflection border-reflection-border text-reflection-foreground" : "bg-navy/5 border-navy/20 text-navy"}`} data-testid={`alert-${alert.type}`}>
            {alert.type === "error" ? <AlertTriangle className="w-4 h-4 shrink-0" /> : alert.type === "warning" ? <AlertCircle className="w-4 h-4 shrink-0" /> : <Info className="w-4 h-4 shrink-0" />}
            <span className="text-sm">{alert.message}</span>
          </div>
        ))}

        {/* Top Row - Stats */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
          {/* Budget Card */}
          <Card className="md:col-span-7 border-border shadow-sm" data-testid="budget-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-gray-900 font-display">
                  This Month's Spending
                </CardTitle>
                <span className="text-xs font-medium uppercase tracking-wider text-gray-400">{summary?.current_month}</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-bold text-gray-900 font-display">
                  ${summary?.current_month_total?.toFixed(2)}
                </span>
                <span className="text-sm text-gray-400">/ ${summary?.monthly_budget?.toFixed(2)}</span>
              </div>
              <div className="mt-4 mb-2 relative">
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <div className={`h-2.5 rounded-full ${budgetColor} transition-all duration-700`} style={{ width: `${Math.min(pct, 100)}%` }}></div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-6">
                <div>
                  <p className="text-xs text-gray-400">Remaining</p>
                  <p className={`text-lg font-semibold ${budgetTextColor} font-display`}>
                    ${summary?.budget_remaining?.toFixed(2)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Used</p>
                  <p className="text-lg font-semibold text-gray-900 font-display">
                    {summary?.budget_percentage?.toFixed(0)}%
                  </p>
                </div>
              </div>
              {summary?.clarity_lock && (
                <div className="mt-4 p-3 bg-reflection rounded-lg flex items-center gap-2 border border-reflection-border">
                  <Lock className="w-4 h-4 text-reflection-foreground shrink-0" />
                  <span className="text-xs font-medium text-reflection-foreground">Clarity Lock is active — budget is locked</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Side Stats */}
          <div className="md:col-span-5 grid grid-cols-2 gap-4">
            <Card className="border-border shadow-sm" data-testid="orders-count-card">
              <CardContent className="pt-6">
                <p className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">Purchases</p>
                <p className="text-2xl font-bold text-gray-900 font-display">{summary?.order_count || 0}</p>
                <p className="text-xs text-gray-400 mt-1">this month</p>
              </CardContent>
            </Card>
            <Card className="border-border shadow-sm" data-testid="impulse-streak-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-1.5 mb-1">
                  <PauseCircle className="w-3.5 h-3.5 text-sage" />
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-400">Reconsidered</p>
                  <StatInfo>
                    The number of times you stepped back from a purchase after a CartControl warning and chose not to go through with it.
                  </StatInfo>
                </div>
                <p className="text-2xl font-bold text-gray-900 font-display">{summary?.impulse_streak || 0}</p>
                <p className="text-xs text-gray-400 mt-1">times you paused</p>
              </CardContent>
            </Card>
            <Card className="border-border shadow-sm" data-testid="budget-streak-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-1.5 mb-1">
                  <Sparkles className="w-3.5 h-3.5 text-warning" />
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-400">Awareness Streak</p>
                  <StatInfo>
                    The number of days in a row you've stayed under your monthly budget.
                  </StatInfo>
                </div>
                <p className="text-2xl font-bold text-gray-900 font-display">{summary?.budget_streak?.value || 0}</p>
                <p className="text-xs text-gray-400 mt-1">days under budget</p>
              </CardContent>
            </Card>
            <Card className="border-border shadow-sm" data-testid="gmail-status-card">
              <CardContent className="pt-6">
                <p className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">Gmail Import</p>
                <Badge variant="outline" className={`text-xs ${summary?.gmail_connected ? "text-sage border-sage bg-sage/10" : "text-gray-500 border-gray-300"}`}>
                  {summary?.gmail_connected ? "Connected" : "Not Connected"}
                </Badge>
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm" variant="outline" className="border-border text-gray-600 hover:bg-muted w-full" onClick={handleSync} disabled={syncing} data-testid="sync-gmail-btn">
                    <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
                    {syncing ? "Importing..." : "Import"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Money-you-kept CTA — encourage saving/investing what wasn't spent.
            Shows generic encouragement now; becomes a partner link once an affiliate
            is configured (referral.partner set). */}
        {referral && (summary?.money_saved > 0 || summary?.budget_remaining > 0) && (
          <Card className="mb-8 border-sage/40 bg-sage/5 shadow-sm" data-testid="referral-cta-card">
            <CardContent className="pt-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div className="flex items-start gap-3">
                <PiggyBank className="w-6 h-6 text-sage shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {summary?.money_saved > 0
                      ? `You've set aside $${summary.money_saved.toFixed(2)} this month.`
                      : `You have $${(summary?.budget_remaining ?? 0).toFixed(2)} left this month.`}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {referral.partner
                      ? `Put the money you didn't spend to work with ${referral.partner}.`
                      : "Consider moving it into savings or investing it — your future self will thank you."}
                  </p>
                </div>
              </div>
              {referral.partner && (
                <div className="flex flex-col items-start sm:items-end gap-1 shrink-0">
                  <Button size="sm" className="bg-sage hover:bg-sage/90 text-white text-xs h-9 px-4" onClick={handleReferralClick} data-testid="referral-cta-btn">
                    Put it to work with {referral.partner}
                  </Button>
                  <span className="text-[10px] text-gray-400">We may earn a commission.</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Spending History Chart — full history for all tiers */}
        {summary?.monthly_breakdown?.length > 1 && (
          <Card className="mb-8 border-border shadow-sm" data-testid="monthly-chart-card">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-gray-900 font-display">Spending History</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={[...summary.monthly_breakdown].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 13% 94%)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#9CA3AF" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#9CA3AF" }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, "Spent"]} contentStyle={{ border: "1px solid hsl(220 13% 91%)", borderRadius: "8px", fontSize: "13px" }} />
                  <Bar
                    dataKey="total"
                    shape={(props) => (
                      <Rectangle
                        {...props}
                        radius={[4, 4, 0, 0]}
                        fill={summary?.monthly_budget > 0 && props.payload.total > summary.monthly_budget ? "#3B4A6B" : "#8FB996"}
                      />
                    )}
                  />
                  {summary?.monthly_budget > 0 && (
                    <ReferenceLine
                      y={summary.monthly_budget}
                      stroke="#3B4A6B"
                      strokeDasharray="5 5"
                      strokeWidth={1.5}
                      ifOverflow="extendDomain"
                      label={{ value: `Budget $${summary.monthly_budget.toFixed(0)}`, position: "insideTopRight", fill: "#3B4A6B", fontSize: 11, fontWeight: 500 }}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Recent Purchases */}
        <Card className="border-border shadow-sm" data-testid="recent-orders-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold text-gray-900 font-display">This Month's Purchases</CardTitle>
            <Button size="sm" className="bg-navy hover:bg-navy-hover active:bg-navy-active text-white text-xs h-9 px-4" onClick={() => setShowAddOrder(true)} data-testid="add-order-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Purchase
            </Button>
          </CardHeader>
          <CardContent>
            {(!summary?.recent_orders || summary.recent_orders.length === 0) ? (
              <div className="text-center py-12">
                <p className="text-sm text-gray-400">No purchases yet. Shop on a connected site, import from Gmail, or add one manually.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-xs font-medium uppercase tracking-wider text-gray-400">Date</TableHead>
                    <TableHead className="text-xs font-medium uppercase tracking-wider text-gray-400">Description</TableHead>
                    <TableHead className="text-xs font-medium uppercase tracking-wider text-gray-400">Retailer</TableHead>
                    <TableHead className="text-xs font-medium uppercase tracking-wider text-gray-400">Source</TableHead>
                    <TableHead className="text-xs font-medium uppercase tracking-wider text-gray-400 text-right">Amount</TableHead>
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.recent_orders.map((order) => {
                    const merged = order.duplicate_status === "confirmed";
                    const twin = order.duplicate_of
                      ? summary.recent_orders.find((o) => o.order_id === order.duplicate_of)
                      : null;
                    return (
                    <Fragment key={order.order_id}>
                    <TableRow className={`hover:bg-muted/50 ${merged ? "opacity-50" : ""}`} data-testid={`order-row-${order.order_id}`}>
                      <TableCell className="text-sm text-gray-600">{order.order_date}</TableCell>
                      <TableCell className="text-sm text-gray-900 font-medium">{order.description || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs text-gray-500 border-border">{order.retailer}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs border ${order.source === "gmail_sync" ? "text-navy border-navy/30 bg-navy/5" : order.source === "extension" ? "text-sage border-sage/40 bg-sage/10" : "text-gray-500 border-border"}`}>
                          {order.source === "gmail_sync" ? "Gmail" : order.source === "extension" ? "Auto" : "Manual"}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-sm font-semibold text-right ${merged ? "text-gray-400 line-through" : "text-gray-900"}`}>${order.amount?.toFixed(2)}</TableCell>
                      <TableCell>
                        {summary?.clarity_lock ? (
                          <span className="text-gray-300 cursor-not-allowed" title="Clarity Lock is active — deletion locked">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                          </span>
                        ) : (
                          <button onClick={() => handleDeleteOrder(order.order_id)} className="text-gray-400 hover:text-danger transition-colors" data-testid={`delete-order-${order.order_id}`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Possible duplicate — a question, not a verdict. Both rows keep counting
                        until the user answers, so nothing here has already changed their total. */}
                    {order.duplicate_status === "suspected" && (
                      <TableRow className="hover:bg-transparent border-0" data-testid={`duplicate-prompt-${order.order_id}`}>
                        <TableCell colSpan={6} className="pt-0 pb-3">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md bg-reflection px-3 py-2">
                            <Info className="w-3.5 h-3.5 shrink-0 text-reflection-foreground" />
                            <span className="text-xs text-reflection-foreground">
                              This might be the same purchase as
                              {twin ? ` the $${twin.amount?.toFixed(2)} ${twin.retailer} on ${twin.order_date}` : " one we already captured"}.
                              Both are counted for now.
                            </span>
                            <div className="ml-auto flex items-center gap-2">
                              <Button size="sm" variant="outline" className="h-7 px-3 text-xs border-reflection-foreground/30 text-reflection-foreground hover:bg-reflection-foreground/10"
                                onClick={() => handleResolveDuplicate(order.order_id, "confirmed")}
                                data-testid={`duplicate-same-${order.order_id}`}>
                                Same one
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-3 text-xs text-reflection-foreground hover:bg-reflection-foreground/10"
                                onClick={() => handleResolveDuplicate(order.order_id, "dismissed")}
                                data-testid={`duplicate-keep-${order.order_id}`}>
                                Keep both
                              </Button>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}

                    {merged && (
                      <TableRow className="hover:bg-transparent border-0" data-testid={`duplicate-merged-${order.order_id}`}>
                        <TableCell colSpan={6} className="pt-0 pb-3">
                          <div className="flex items-center gap-3 px-3 text-xs text-gray-500">
                            <span>Not counted — you marked this as the same purchase.</span>
                            <button className="underline hover:text-gray-700"
                              onClick={() => handleResolveDuplicate(order.order_id, "dismissed")}
                              data-testid={`duplicate-undo-${order.order_id}`}>
                              Undo
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Purchase Dialog */}
      <Dialog open={showAddOrder} onOpenChange={setShowAddOrder}>
        <DialogContent className="sm:max-w-md" data-testid="add-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add Purchase</DialogTitle>
            <DialogDescription>Record a purchase that wasn't captured automatically or imported from Gmail.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddOrder} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-gray-700">Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="29.99"
                className="mt-1.5 h-11 border-border"
                value={orderForm.amount}
                onChange={(e) => setOrderForm({ ...orderForm, amount: e.target.value })}
                required
                data-testid="order-amount-input"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-700">Retailer</Label>
              <Input
                type="text"
                placeholder="Amazon"
                className="mt-1.5 h-11 border-border"
                value={orderForm.retailer}
                onChange={(e) => setOrderForm({ ...orderForm, retailer: e.target.value })}
                data-testid="order-retailer-input"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-700">Description (optional)</Label>
              <Input
                type="text"
                placeholder="USB-C Cable"
                className="mt-1.5 h-11 border-border"
                value={orderForm.description}
                onChange={(e) => setOrderForm({ ...orderForm, description: e.target.value })}
                data-testid="order-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddOrder(false)} data-testid="cancel-order-btn">Cancel</Button>
              <Button type="submit" className="bg-navy hover:bg-navy-hover active:bg-navy-active text-white" data-testid="submit-order-btn">Add Purchase</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
